import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { SURVEY_TEMPLATES, SURVEY_TYPES } from 'constants/surveys';
import { useGetSurveyStatus } from 'hooks/useGetSurveyStatus';
import { useSaveSurveyResponse } from 'hooks/useSaveSurveyResponse';
import { useUserStore } from 'stores/user';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import type { OptionDTO } from 'types/surveys';
import { TrackingEventsType } from 'types/tracking';
import { RadioButtons } from './components/RadioButtons';
import { TextArea } from './components/TextArea';
import { VerticalOptions } from './components/VerticalOptions';
import { getSurveyContent } from './utils';

const COMPONENT_MAP = {
  [SURVEY_TEMPLATES.RADIO_BUTTONS]: RadioButtons,
  [SURVEY_TEMPLATES.VERTICAL_OPTIONS]: VerticalOptions,
  [SURVEY_TEMPLATES.TEXT_AREA]: TextArea,
} as const;

enum SurveyState {
  IDLE = 'idle',
  ACTIVE = 'active',
  COMPLETED = 'completed',
}

export const NPSSurvey = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.HOME);
  const { trackSurveyQuestionAnswered } = useTrackingEvents();
  const userId = useUserStore(state => state.user?.id);
  const [surveyState, setSurveyState] = useState<SurveyState>(SurveyState.IDLE);

  const { survey, surveyResponse, isLoading, isCompleted } = useGetSurveyStatus(
    SURVEY_TYPES.ASSESSMENT,
    false
  );
  const { mutateAsync: saveSurveyResponse } = useSaveSurveyResponse(survey, {
    surveyType: SURVEY_TYPES.ASSESSMENT,
  });

  const currentQuestionIndex = surveyResponse?.currentQuestionIndex ?? 0;
  const currentQuestion = survey?.questions?.[currentQuestionIndex];
  const Component = currentQuestion
    ? COMPONENT_MAP[currentQuestion.template as keyof typeof COMPONENT_MAP]
    : null;
  const isStarted = currentQuestionIndex > 0 || surveyState === SurveyState.ACTIVE;
  const isLastQuestion = currentQuestionIndex === Number(survey?.questions?.length) - 1;

  const { title, heading, image, description } = getSurveyContent(
    isCompleted,
    isStarted,
    currentQuestionIndex,
    survey
  );

  const handleOnSaveResponse = async (option: OptionDTO, text?: string): Promise<void> => {
    await saveSurveyResponse({
      questionId: currentQuestion?.id || '',
      optionIds: [option.id],
      requiredTextValue: text ?? undefined,
    });

    trackSurveyQuestionAnswered(
      {
        question: currentQuestion?.text || '',
        surveyType: SURVEY_TYPES.ASSESSMENT,
        answer: option.text + text,
        userId,
      },
      TrackingEventsType.NPS_QUESTION_ANSWERED
    );

    if (isLastQuestion) {
      setSurveyState(SurveyState.COMPLETED);
    }
  };

  const Heading = isStarted ? 'p' : 'h4';
  const showQuestion = isStarted && !isCompleted;
  const showStartButton = !isStarted && !isCompleted;

  if (isLoading || (isCompleted && surveyState !== SurveyState.COMPLETED)) {
    return null;
  }

  return (
    <AnimatedContainer>
      <Card variant="secondary" withoutBorder className="am-md:px-10 am-md:py-6 p-6">
        <div className="flex gap-10 items-start">
          <img
            src={image}
            alt="NPS Survey Icon"
            className={cn('hidden am-lg:block mt-4', isCompleted && 'mt-0')}
          />
          <div
            className={cn(
              'flex flex-col items-end gap-8 am-lg:gap-10 w-full min-h-40',
              isCompleted && 'min-h-fit'
            )}
          >
            <div className="flex flex-col gap-2 w-full">
              <p className="text-xs text-denim-300 leading-4 am-lg:text-sm mb-2 am-lg:mb-4">
                {title}
              </p>
              <Heading
                className={cn(
                  'text-denim-800 font-medium leading-5 am-lg:max-w-180 am-lg:text-xl am-lg:leading-6 am-lg:text-denim-500',
                  isStarted ? 'am-lg:text-lg font-medium' : 'am-lg:text-xl'
                )}
              >
                {heading}
              </Heading>
              {showQuestion ? (
                currentQuestion &&
                Component && (
                  <Component
                    options={currentQuestion.optionsDTO}
                    onSaveResponse={handleOnSaveResponse}
                  />
                )
              ) : (
                <p className="text-denim-500 text-sm leading-4 am-lg:leading-5 am-lg:text-base">
                  <Trans i18nKey={description} ns={LOCIZE_NS.HOME} />
                </p>
              )}
            </div>
            {showStartButton && (
              <Button
                variant="link"
                className="w-fit text-denim-400 hover:text-teal-500 gap-2 p-0"
                onClick={() => setSurveyState(SurveyState.ACTIVE)}
              >
                {t('NPSSurvey.Initial.cta')}
                <ChevronRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    </AnimatedContainer>
  );
};
